import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Dialog, DialogContent } from '@mui/material';

import { navigate, useParams } from '@redwoodjs/router';
import { MetaTags } from '@redwoodjs/web';

import { DocumentUploadHelpBanner } from 'src/components';
import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import { StepperComponent } from 'src/components/StepperComponent/StepperComponent';
import { ADD_MERCHANT_STEPS, Step } from 'src/constants/merchants';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { isIreland } from 'src/lib/countryConfig';
import { updateStepCompletion } from 'src/utils/utils';

import OutletInformation from './steps/OutletInformation';

const AddMerchantPage = () => {
    const { user } = useAuthContext();
    const {
        checkForDraftApplication,
        setMerchantApplication,
        findingDraftApplication,
        draftAvailable,
        steps,
        setSteps,
        activeStep,
        setActiveStep,
        merchantApplication,
        setShouldResetForm,
        setCompletedSteps,
        findingDraftApplicationById,
    } = useAddMerchantContext();

    const [canAddMerchant, setCanAddMerchant] = useState<boolean>(true);

    const [userIsAdmin, setUserIsAdmin] = useState<boolean>(false);

    const [isDraftApplicationAvailable, setIsDraftApplicationAvailable] = useState<boolean>(false);

    const showDraftApplicationAvailableModal = useRef(false);

    const { merchantApplicationId, step: isDraftApplicationStep } = useParams();

    const closeNotifModal = () => {
        navigate('/');
    };

    const addSellerId = () => {
        navigate(`/user-management/users/${user?.id}`);
    };

    const getIsCompletedValues = (payload) => {
        return {
            businessInformation: payload?.businessInformation?.isStepCompleted,
            merchantInformation: payload?.merchantInformation?.isStepCompleted,
            outletInformation: payload?.outletInformation?.isStepCompleted,
            serviceRequirements: payload?.serviceRequirements?.isStepCompleted,
            supportingDocuments: payload?.supportingDocuments?.isStepCompleted,
        };
    };

    const hasOutletInformation = merchantApplication?.outletInformation;

    const OUTLET_INFO_STEP = useMemo(() => {
        return {
            label: 'Outlet Information',
            completed: false,
            accessor: 'outletInformation',
            form: <OutletInformation key="outlet-information" handleBack={() => {}} />,
        };
    }, []);

    const isCompletedValues = getIsCompletedValues(merchantApplication);

    const updatedSteps = useMemo(() => {
        const updatedValues = updateStepCompletion(
            hasOutletInformation
                ? [
                      ...ADD_MERCHANT_STEPS.slice(0, 3),
                      OUTLET_INFO_STEP,
                      ...ADD_MERCHANT_STEPS.slice(3),
                  ]
                : ADD_MERCHANT_STEPS,
            isCompletedValues
        );

        return updatedValues;
    }, [OUTLET_INFO_STEP, hasOutletInformation, isCompletedValues]);

    useEffect(() => {
        checkForDraftApplication();
    }, []);

    // Check if the user has a seller id. If they are an admin user they can add it to their account.
    useEffect(() => {
        if (user) {
            if (user?.role?.hierarchyLevel === 'NPI' && !user?.sellerId) {
                setUserIsAdmin(true);
                setCanAddMerchant(false);
            } else if (!user?.sellerId) {
                setCanAddMerchant(false);
            }
        }
    }, [user]);

    // Scroll to the start of the new form
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeStep]);

    useEffect(() => {
        if (!showDraftApplicationAvailableModal.current && draftAvailable) {
            setCompletedSteps();
            setIsDraftApplicationAvailable(true);
            showDraftApplicationAvailableModal.current = true;
        }
    }, [draftAvailable, merchantApplicationId, setCompletedSteps]);

    return (
        <PageContent>
            {(findingDraftApplication || findingDraftApplicationById) && <Loader />}
            <MetaTags title="AddMerchant" description="AddMerchant page" />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: '24px',
                }}
            >
                <StepperComponent
                    stepsList={merchantApplicationId ? updatedSteps : steps}
                    activeStep={activeStep}
                    showStepLabel={false}
                    setActiveStep={setActiveStep}
                />
                {steps[activeStep]?.accessor === 'supportingDocuments' && isIreland ? (
                    <DocumentUploadHelpBanner
                        businessType={
                            merchantApplication?.merchantInformation?.aboutBusiness?.businessType
                        }
                    />
                ) : null}
                {steps[activeStep]?.form}
            </Box>

            <Dialog
                open={!canAddMerchant}
                onClose={closeNotifModal}
                aria-labelledby="show-add-merchant-dialog-notif"
                sx={{
                    '& .MuiDialog-paper': {
                        width: '400px',
                    },
                }}
            >
                <DialogContent>
                    <ModalContent
                        handleClose={closeNotifModal}
                        handleAction={addSellerId}
                        closeModalButtonText="OK"
                        isActionDisabled={!userIsAdmin}
                        isEdit
                        header="Cannot add a merchant"
                        message={
                            !userIsAdmin
                                ? `You do not have a seller id configured with your account.
            \n Please contact an administrator to update your account with a seller id`
                                : `You do not have a seller id configured with your account.`
                        }
                        buttonText="Add Seller ID"
                    />
                </DialogContent>
            </Dialog>
            {/* Second Dialog for draft applications */}
            {!findingDraftApplication && activeStep > 0 && (
                <Dialog
                    open={isDraftApplicationAvailable && !isDraftApplicationStep}
                    onClose={() => {
                        return setIsDraftApplicationAvailable(false);
                    }}
                    aria-labelledby="show-draft-merchant-dialog-notif"
                    sx={{
                        '& .MuiDialog-paper': {
                            width: '400px',
                        },
                    }}
                >
                    <DialogContent>
                        <ModalContent
                            handleClose={() => {
                                // Continue draft application
                                setShouldResetForm(false);
                                setIsDraftApplicationAvailable(false);
                            }}
                            handleAction={() => {
                                // Create new application
                                setShouldResetForm(true);
                                setMerchantApplication(null);
                                setIsDraftApplicationAvailable(false);
                                setActiveStep(0);
                                setSteps(
                                    ADD_MERCHANT_STEPS.map((step: Step) => {
                                        step.completed = false;
                                        return step;
                                    })
                                );
                            }}
                            closeModalButtonText="Continue Application"
                            isEdit
                            header="We found an incomplete application"
                            message={
                                'Would you like to continue with the last application or start a new one?'
                            }
                            buttonText="Start New Application"
                        />
                    </DialogContent>
                </Dialog>
            )}
        </PageContent>
    );
};

export default AddMerchantPage;
